'use client'

// libraries
import { useRef, useEffect, useId, useState } from 'react'
import clsx from 'clsx'
import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import { Mousewheel, Navigation } from 'swiper/modules'
import { gsap } from 'gsap'
import { useGSAP } from '@gsap/react'
import 'swiper/css'

// components
import Section from '/react4xp/components/Section'
import Link from '/react4xp/components/Link'
import Card, { CardRef } from '/react4xp/components/Card'

// svg
import { ChevronLeft, ChevronRight } from '/react4xp/components/Icons/Ux'

// css
import './index.scss'

// types
export interface ContentSliderProps {
    lazyLoadVideo?: boolean
    componentId?: string
    isAnimated?: boolean
    className?: string
    title?: string
    intro?: string
    link?: {
        label?: string
        href?: string
        external?: boolean
    }
    cards?: {
        variation?: 'media' | 'news'
        media?: {
            color?: 'dark-green' | 'light-green'
            icon?: 'image' | 'video' | 'article'
            image?: {
                src?: string
                alt?: string
            }
            video?: string
            youtubeVideoId?: string
            title?: string
            link?: {
                href?: string
                external?: boolean
            }
        }[]
        news?: {
            image?: {
                src?: string
                alt?: string
            }
            title?: string
            intro?: string
            link?: {
                href?: string
                external?: boolean
            }
        }[]
    }
    singleStrings?: {
        previous?: string
        next?: string
    }
}

export default function ContentSlider({
    lazyLoadVideo = false,
    componentId,
    isAnimated,
    className,
    title,
    intro,
    link,
    cards,
    singleStrings
}: ContentSliderProps) {

    const uniqueId = useId()
    const swiperRef = useRef<SwiperType | null>(null)
    const prevButtonRef = useRef<HTMLButtonElement>(null)
    const nextButtonRef = useRef<HTMLButtonElement>(null)
    const cardRefs = useRef<(CardRef | null)[]>([])
    const cardsRef = useRef<HTMLDivElement>(null)
    const [currentSlide, setCurrentSlide] = useState(0)
    const [isMobile, setIsMobile] = useState(false)

    const sanitizedId = (componentId || uniqueId.replace(/:/g, '')).replace(/[^a-zA-Z0-9_-]/g, '-')
    const prevButtonClass = `content-slider-component__prev-${sanitizedId}`
    const nextButtonClass = `content-slider-component__next-${sanitizedId}`
    
    // check if screen is below 768px
    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth < 768)
        }
        
        checkScreenSize()
        window.addEventListener('resize', checkScreenSize)
        
        return () => window.removeEventListener('resize', checkScreenSize)
    }, [])

    useGSAP((_context, contextSafe) => {
        const slider = cardsRef.current?.querySelector('[data-stagger-left-slider="true"]')
        if (!slider || !contextSafe) return

        const revealSlides = contextSafe(() => {
            gsap.fromTo(slider.querySelectorAll('.swiper-slide'),
                {
                    transform: 'translateX(100%)',
                    opacity: 0
                },
                {
                    transform: 'translateX(0)',
                    opacity: 1,
                    duration: 1,
                    delay: 0.2,
                    stagger: 0.175,
                    ease: 'circ.out'
                }
            )
        })

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return

                observer.disconnect()
                revealSlides()
            },
            {
                threshold: 0.1,
                rootMargin: '0px 0px -10% 0px'
            }
        )

        observer.observe(slider)

        return () => observer.disconnect()
    })

    // handle video playing based on screen size and current slide
    const playVideoOnSlide = (slideIndex: number) => {
        if (!cards?.media) return

        cardRefs.current.forEach((cardRef, index) => {
            if (cardRef && cardRef.hasVideo()) {
                if (index === slideIndex) {
                    cardRef.playVideo();
                } else {
                    cardRef.pauseVideo();
                }
            }
        })
    }

    // play video when slide changes
    useEffect(() => {
        if (!lazyLoadVideo) {
            playVideoOnSlide(currentSlide)
        }
    }, [currentSlide])

    // handle mouse enter for desktop
    const handleMouseEnter = (index: number) => {
        if (!isMobile) {
            const hoveredCard = cardRefs.current[index]
            
            // only control videos if the hovered card has a video
            if (hoveredCard && hoveredCard.hasVideo()) {
                cardRefs.current.forEach((cardRef, cardIndex) => {
                    if (cardRef && cardRef.hasVideo()) {
                        if (cardIndex === index) {
                            cardRef.playVideo();
                        } else {
                            cardRef.pauseVideo();
                        }
                    }
                })
            }
        }
    }

    return (
        <Section className={clsx('content-slider-component', className)}>

            <div className='content-slider-component__top'>
                <div className='container'>
                    <div className='content-slider-component__top__wrapper'>

                        <div className='content-slider-component__top__left'>

                            {title && (
                                <h2 className='h2'>
                                    {title}
                                </h2>
                            )}

                            {intro && (
                                <div
                                    className='h3'
                                    dangerouslySetInnerHTML={{ __html: intro }}
                                    suppressHydrationWarning
                                />
                            )}

                            {link?.href && cards?.variation === 'media' && (
                                <Link
                                    href={link.href}
                                    label={{
                                        underline: link.label
                                    }}
                                    isExternal={link.external}
                                    icon={{
                                        type: 'arrow-right'
                                    }}
                                    className='body'
                                />
                            )}

                        </div>

                        <div className={clsx(
                            'content-slider-component__top__right',
                            ((cards?.media?.length || cards?.news?.length || 0) === 2 && 'content-slider-component__top__right--mobile-only') ||
                            ((cards?.media?.length || cards?.news?.length || 0) <= 1 && 'hidden')
                        )}>

                            <button
                                ref={prevButtonRef}
                                type='button'
                                aria-label={singleStrings?.previous}
                                className={`content-slider-component__prev ${prevButtonClass}`}
                            >
                                <ChevronLeft />
                            </button>

                            <button
                                ref={nextButtonRef}
                                type='button'
                                aria-label={singleStrings?.next}
                                className={`content-slider-component__next ${nextButtonClass}`}
                            >
                                <ChevronRight />
                            </button>

                        </div>

                    </div>
                </div>
            </div>

            <div ref={cardsRef} className='content-slider-component__cards'>
                <div className='container'>
                    
                    <Swiper
                        modules={[Mousewheel, Navigation]}
                        className='content-slider-component__slider'
                        spaceBetween={32}
                        slidesPerView={1.2}
                        initialSlide={0}
                        autoHeight
                        allowTouchMove
                        data-stagger-left-slider={isAnimated ? 'true' : 'false'}
                        onSwiper={(swiper) => {
                            swiperRef.current = swiper
                        }}
                        onInit={(swiper) => {
                            if (prevButtonRef.current && nextButtonRef.current) {
                                swiper.navigation.init()
                                swiper.navigation.update()
                            }
                        }}
                        onSlideChange={(swiper) => {
                            setCurrentSlide(swiper.activeIndex)
                        }}
                        mousewheel={{
                            forceToAxis: true
                        }}
                        navigation={{
                            nextEl: `.${nextButtonClass}`,
                            prevEl: `.${prevButtonClass}`
                        }}
                        breakpoints={{
                            768: {
                                slidesPerView: 2
                            },
                            1200: {
                                slidesPerView: 2.7
                            }
                        }}
                    >

                        {cards?.variation === 'media' && cards?.media?.map((item, i) => (
                            <SwiperSlide key={i}>
                                <Card
                                    variation='media'
                                    image={item?.image}
                                    video={item?.video}
                                    youtubeVideoId={item?.youtubeVideoId}
                                    youtubeVideoAutoplay={i === 0}
                                    color={item?.color}
                                    icon={item?.icon}
                                    title={item?.title}
                                    link={item?.link}
                                    ref={(el: CardRef | null) => { cardRefs.current[i] = el }}
                                    onMouseEnter={() => handleMouseEnter(i)}
                                    lazyLoadVideo={lazyLoadVideo}
                                />
                            </SwiperSlide>
                        ))}

                        {cards?.variation === 'news' && cards?.news?.map((item, i) => (
                            <SwiperSlide key={i}>
                                <Card
                                    variation='news'
                                    image={item?.image}
                                    title={item?.title}
                                    intro={item?.intro}
                                    link={item?.link}
                                />
                            </SwiperSlide>
                        ))}

                    </Swiper>

                    {link?.href && cards?.variation === 'news' && (
                        <Link
                            href={link.href}
                            label={{
                                underline: link.label
                            }}
                            isExternal={link.external}
                            icon={{
                                type: 'arrow-right'
                            }}
                            className='body content-slider-component__bottom-link'
                        />
                    )}

                </div>
            </div>

        </Section>
    )
}